import React, { useCallback, useEffect } from "react";
import { AdminHead } from "../../components/admin/AdminHead";
import Box from "@carrier-io/air-react/Box";
import { Clients, useMutation, apollo } from "../../apollo";
import { CreateUserMutation } from "../../graphql/mutations/createUser";
import { DEFAULT_REGION } from "../../constants/regions";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes } from "../../contexts/alert/types";
import { useNavigate } from "react-router-dom";
import { RoutesPage } from "../../constants/routes";
import { displayError } from "../../constants/error";
import { useForm } from "react-hook-form";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { UserInput } from "../../validations/userCreateValidation";
import { AuthMethod } from "../../generated/ups/graphql";
import { useDebounce } from "use-debounce";
import { isEmail } from "class-validator";
import { useSearchUserByEmail } from "../../hooks/useSearchUserByEmail";
import { UserForm } from "../../components/userForm/UserForm";

export const CreateUser = () => {
  const navigate = useNavigate();
  const { pushAlert } = useAlerts();
  const { refetch: fetchUserByEmail } = useSearchUserByEmail();
  const resolver = classValidatorResolver<UserInput>(UserInput);
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid },
  } = useForm<UserInput>({
    mode: "onBlur",
    resolver,
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      authMethod: "" as AuthMethod,
      groups: [],
      roles: [],
      attributes: [],
    },
  });

  const [createUser, { loading }] = useMutation(
    Clients.UPS,
    CreateUserMutation,
  );

  const email = watch("email");
  const [debouncedEmail] = useDebounce(email, 300);

  const verifyEmail = useCallback(
    async (email: string) => {
      const res = await fetchUserByEmail(email);
      if (res.data.searchUsers.users.length) {
        setError("email", {
          type: "custom",
          message: "Email already exists",
        });
      } else {
        clearErrors("email");
      }
    },
    [clearErrors, fetchUserByEmail, setError],
  );

  useEffect(() => {
    if (!isEmail(debouncedEmail)) return;

    verifyEmail(debouncedEmail);
  }, [debouncedEmail, verifyEmail]);

  const handleAddUser = async (data: UserInput) => {
    const attributes = data.attributes?.reduce(
      (acc, item) => {
        acc[item.key] = item.value;
        return acc;
      },
      {} as Record<string, string>,
    );

    const user = {
      ...data,
      attributes,
    };

    try {
      await createUser({
        variables: {
          user,
          region: DEFAULT_REGION,
        },
      });
      pushAlert(AlertTypes.SUCCESS, "User added successfully");
      navigate(RoutesPage.UserManagement);
    } catch (error: unknown) {
      pushAlert(
        AlertTypes.ERROR,
        displayError((error as apollo.ApolloError).graphQLErrors[0]),
      );
    }
  };

  return (
    <>
      <AdminHead title="Add a New User" />
      <Box style={{ padding: "24px 32px 40px", maxWidth: "922px" }}>
        <UserForm
          control={control}
          errors={errors}
          onSubmit={handleSubmit(handleAddUser)}
          loading={loading}
          isValid={isValid}
          onCancel={() => navigate(RoutesPage.UserManagement)}
        />
      </Box>
    </>
  );
};
